/**
 * Hook for the initialiseProject mutation and progress polling.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { gql, useMutation } from "@apollo/client";

import { getAuthToken } from "@/lib/getAuthToken";

const INITIALISE_PROJECT_ASYNC = gql`
  mutation InitialiseProjectAsync($input: InitialiseProjectInput!) {
    initialiseProjectAsync(input: $input) {
      success
      message
      operationId
    }
  }
`;

/**
 * REST endpoint for progress polling.
 *
 * This intentionally bypasses GraphQL to avoid checking out a DB connection
 * on every poll.  The long-running initialise_project mutation holds a DB
 * connection for its entire duration; if polling also grabs connections the
 * pool (5+10 = 15) gets exhausted and the backend deadlocks.
 *
 * Uses VITE_API_URL so the request goes directly to the backend (same as
 * GraphQL), rather than through the Vite proxy which can't reach the backend
 * from inside a Docker container via localhost.
 */
const API_BASE = import.meta.env.VITE_API_URL || "";
const PROGRESS_API_BASE = `${API_BASE}/api/progress`;

export interface WorkSectionSpaceInitInput {
  schedulingSpaceId: string;
  includeOsakohteenTarkastus: boolean;
  includeMalliasennustarkastus: boolean;
  includeMestanVastaanotto: boolean;
  includeTyovaiheenvastaanotto: boolean;
  includeLiitedokumentit: boolean;
}

export interface SpaceResponsibleInitInput {
  /** External scheduling-space id (NOT a DB location id). The worker resolves
   *  it to a Location and sets/creates the section-location's responsible. */
  schedulingSpaceId: string;
  responsibleUserId: string;
}

export interface WorkSectionInitInput {
  sectionCode: string;
  sectionName: string;
  templateWorkSectionId?: string;
  spaces: WorkSectionSpaceInitInput[];
  plannedStart?: string | null;
  plannedEnd?: string | null;
  /** Per-WS inspector (Tarkastaja). When null/omitted the backend falls back
   *  to the parent PP's responsibleUserId. */
  responsibleUserId?: string | null;
  /** Staged section-location responsibles, keyed by schedulingSpaceId. */
  spaceResponsibles?: SpaceResponsibleInitInput[];
}

export interface ProcurementPackageInitInput {
  templateProcurementPackageId: string;
  responsibleUserId: string;
  code: string;
  name?: string | null;
  includeSuunnitelmiinPerehtyminen: boolean;
  includeAliurakkaPerehtyminen: boolean;
  includeAloitusedellytykset: boolean;
  includeTaloudellinenLoppuselvitys: boolean;
  workSections: WorkSectionInitInput[];
  plannedStart?: string | null;
  plannedEnd?: string | null;
}

export interface PlotInitInput {
  name: string;
  locationSchedulingSpaceId: string | null;
  procurementPackages: ProcurementPackageInitInput[];
}

export interface SpaceHierarchyInput {
  schedulingSpaceId: string;
  name: string;
  parentSchedulingSpaceId?: string | null;
  spaceType?: string | null;
}

export interface InitialiseProjectInput {
  name: string;
  projectType: string;
  taskTemplateSetId: string;
  schedulingProjectId: string;
  tyopaallikkoUserId: string;
  vastaavaTyonjohtajaUserId: string;
  description?: string;
  startDate?: string;
  objectiveEndDate?: string;
  plots: PlotInitInput[];
  allSpaces: SpaceHierarchyInput[];
  // Inspection platform fields (optional)
  inspectionProjectOwnerId?: string;
  existingInspectionProjectId?: string;
  inspectionMatrixId?: string;
  inspectionTemplateOwnerProjectId?: string;
  inspectionProjectCode?: string;
  // Progress tracking
  operationId?: string;
}

export interface InitialisationProgress {
  step: number;
  totalSteps: number;
  stepLabel: string;
  detail: string;
  itemsDone: number;
  itemsTotal: number;
  error?: string | null;
  complete?: boolean;
}

export function useInitialiseProject() {
  const [mutate, { loading, error }] = useMutation(INITIALISE_PROJECT_ASYNC, {
    refetchQueries: ["GetProjects"],
  });

  const initialiseProject = async (input: InitialiseProjectInput) => {
    const result = await mutate({ variables: { input } });
    return result.data?.initialiseProjectAsync;
  };

  return { initialiseProject, loading, error };
}

/**
 * Hook that polls the backend for initialisation progress while the worker is running.
 *
 * Uses a plain `fetch` to a lightweight REST endpoint (/api/progress/:id) instead of
 * Apollo/GraphQL.  This is critical because every GraphQL request checks out a DB
 * connection (via the context getter), and the long-running mutation already holds one.
 * Polling via GraphQL would exhaust the connection pool and deadlock the backend.
 */
export function useInitialisationProgress(
  operationId: string | null,
  polling: boolean,
  onComplete?: () => void,
  onError?: (error: string) => void,
) {
  const [progress, setProgress] = useState<InitialisationProgress | null>(null);
  const [isComplete, setIsComplete] = useState(false);
  const [workerError, setWorkerError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Keep operationId in a ref so the interval callback always sees the latest value
  const operationIdRef = useRef(operationId);
  operationIdRef.current = operationId;

  const doFetch = useCallback(async () => {
    const opId = operationIdRef.current;
    if (!opId) return;
    try {
      // Attach the bearer token: in prod the AuthMiddleware rejects tokenless
      // requests with 401 (dev lets them through via debug mode). Same pattern
      // as useMyInitialisations. The endpoint reads Redis only — no DB.
      const token = await getAuthToken();
      const res = await fetch(`${PROGRESS_API_BASE}/${opId}`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (!res.ok) return;
      const data = await res.json();
      // Empty object means no progress written yet
      if (data && data.step != null) {
        const newProgress: InitialisationProgress = {
          step: data.step,
          totalSteps: data.totalSteps,
          stepLabel: data.stepLabel,
          detail: data.detail ?? "",
          itemsDone: data.itemsDone ?? 0,
          itemsTotal: data.itemsTotal ?? 0,
          error: data.error ?? null,
          complete: data.complete ?? false,
        };
        setProgress(newProgress);

        // Check for completion
        if (newProgress.complete || newProgress.step >= newProgress.totalSteps) {
          setIsComplete(true);
          if (newProgress.error) {
            setWorkerError(newProgress.error);
            onError?.(newProgress.error);
          } else {
            onComplete?.();
          }
        }
      }
    } catch {
      // Silently ignore polling errors — the mutation result is what matters
    }
  }, [onComplete, onError]);

  // Start/stop polling based on the `polling` flag and completion state
  useEffect(() => {
    if (polling && operationId && !isComplete) {
      // Poll immediately, then every 1.5 seconds
      doFetch();
      intervalRef.current = setInterval(doFetch, 1500);
    } else {
      // Stop polling
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [polling, operationId, isComplete, doFetch]);

  const reset = useCallback(() => {
    setProgress(null);
    setIsComplete(false);
    setWorkerError(null);
  }, []);

  return { progress, isComplete, workerError, reset };
}

const CANCEL_INITIALIZATION = gql`
  mutation CancelInitialization($operationId: String!) {
    cancelInitialization(operationId: $operationId) {
      success
      message
      operationId
    }
  }
`;

export function useCancelInitialization() {
  const [mutate, { loading, error }] = useMutation(CANCEL_INITIALIZATION);

  const cancelInitialization = async (operationId: string) => {
    const result = await mutate({ variables: { operationId } });
    return result.data?.cancelInitialization;
  };

  return { cancelInitialization, loading, error };
}
